import Form from "../../Helpers/Form/Form";
import { Offcanvas } from "react-bootstrap";
import FormHeader from "../../Helpers/Form/FormParts/FormHeader";
import trans, { isRTL } from "../../../utils";
import SnagFormBody from "./SnagFormBody";
import {
	useBulkCreateSnagsMutation,
	useUpdateSnagMutation,
	useGetSpecificSnagQuery,
} from "../../../Redux/service/Snag/Snag";
import { Formik, Form as FormFormik } from "formik";
import Button from "../../Helpers/Button/Button";
import * as Yup from "yup";
import { useContext, useEffect, useState } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { toast } from "react-toastify";
import useIsMobile from "../../../hooks/useIsMobile";

export default function FormSnag({
	snagForm,
	setSnagForm,
	preselectedLocation = null,
	editingSnagId = null,
	isEditMode = false,
}) {
	const isMobile = useIsMobile();
	const { setEdited } = useContext(popupcontext);
	const [bulkCreateSnags] = useBulkCreateSnagsMutation();
	const [updateSnag] = useUpdateSnagMutation();

	// ✅ Fetch snag data when editing
	const {
		data: snagData,
		isLoading: isLoadingSnag,
		isFetching: isFetchingSnag,
	} = useGetSpecificSnagQuery(editingSnagId, {
		skip: !isEditMode || !editingSnagId,
	});

	console.log("FormSnag - preselectedLocation:", preselectedLocation);
	console.log("FormSnag - isEditMode:", isEditMode);
	console.log("FormSnag - editingSnagId:", editingSnagId);
	console.log("FormSnag - snagData:", snagData);

	// ✅ Create initial values based on mode
	const getInitialValues = () => {
		if (isEditMode && snagData?.data) {
			const snag = snagData.data;

			console.log("🔍 Mapping snag data to form values:");
			console.log("Backend -> Frontend mapping:");
			console.log("project_id:", snag.project_id, "->", snag.project_id);
			console.log("zone_id:", snag.zone_id, "->", snag.zone_id, "(Building)");
			console.log(
				"level_id:",
				snag.level_id,
				"->",
				snag.level_id,
				"(Level/building_id in form)",
			);
			console.log(
				"room_id:",
				snag.room_id,
				"->",
				snag.room_id,
				"(Space/level in form)",
			);
			console.log("assigned_team_id:", snag.assigned_team_id);
			console.log("attachments count:", snag.attachments?.length || 0);

			return {
				project_id: snag.project_id || "",
				zone_id: snag.zone_id || "", // Building
				building_id: snag.level_id || "", // Level (backend level_id -> frontend building_id)
				level: snag.room_id || "", // Space (backend room_id -> frontend level)
				snags: [
					{
						sector: snag.sector || "",
						assigned_team_id: snag.assignedTo?.name || "", //
						subsystem: snag.subsystem || "",
						discipline: snag.discipline || "",
						item: snag.item || "",
						description: snag.description || "",
						asset_tag_id: snag.asset_tag_id || "",
						drawing_ref: snag.drawing_ref || "",
						area_room: snag.area_room || "",
						location: snag.location || "",
						severity: snag.severity || "",
						acceptance_criteria: snag.acceptance_criteria || "",
						inspection_method: snag.inspection_method || "",
						location_details: snag.location_details || "",
						target_date: snag.target_date || "",
						files: [],
						existingAttachments: snag.attachments || [], // ✅ Store existing attachments
						delete_attachment_ids: [], // ✅ Track attachments to delete
					},
				],
				// ✅ Store location names for display in edit mode
				project_name: snag.project?.name || "",
				zone_name: snag.zone?.name || "",
				building_name: snag.level?.name || "",
				space_name: snag.room?.name || "",
			};
		}

		// Create mode with preselected location
		return {
			project_id: preselectedLocation?.project_id || "",
			zone_id: preselectedLocation?.zone_id || "",
			building_id: preselectedLocation?.building_id || "",
			level: preselectedLocation?.level || preselectedLocation?.area_room || "",
			snags: [
				{
					sector: "",
					assigned_team_id: "",
					subsystem: "",
					discipline: "",
					item: "",
					description: "",
					asset_tag_id: "",
					drawing_ref: "",
					area_room: preselectedLocation?.area_room || "",
					location: preselectedLocation?.location || "",
					severity: "",
					acceptance_criteria: "",
					inspection_method: "",
					location_details: "",
					target_date: "",
					files: [],
				},
			],
		};
	};

	const snagSchema = Yup.object({
		sector: Yup.string().required(trans("Sector is required")),
		assigned_team_id: Yup.string().required(trans("Assign To is required")),
		subsystem: Yup.string().notRequired(),
		discipline: Yup.string().notRequired(),
		item: Yup.string().notRequired(),
		description: Yup.string().required(trans("Description is required")),
		asset_tag_id: Yup.string().notRequired(),
		drawing_ref: Yup.string().notRequired(),
		area_room: Yup.string().notRequired(),
		location: Yup.string().notRequired(),
		severity: Yup.string().notRequired(),
		acceptance_criteria: Yup.string().notRequired(),
		inspection_method: Yup.string().notRequired(),
		location_details: Yup.string().notRequired(),
		target_date: Yup.date().notRequired(),
		files: Yup.array().of(Yup.mixed()).notRequired(),
	});

	const validationSchema = Yup.object({
		project_id: Yup.string().required(trans("Project is required")),
		zone_id: Yup.string().notRequired(),
		building_id: Yup.string().notRequired(),
		level: Yup.string().notRequired(),
		snags: Yup.array()
			.of(snagSchema)
			.min(1, trans("At least one snag is required")),
	});

	const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
		setSubmitting(true);

		try {
			const formData = new FormData();

			// =====================================================
			// CORRECT FIELD MAPPING TO BACKEND
			// =====================================================
			// Frontend Form → Backend API
			// project_id    → project_id   ✅
			// zone_id       → zone_id      ✅ (Building in UI)
			// building_id   → level_id     ✅ (Level in UI)
			// level         → room_id      ✅ (Space in UI)
			// =====================================================

			formData.append("project_id", values.project_id);

			if (values.zone_id) {
				formData.append("zone_id", values.zone_id);
			}

			if (values.building_id) {
				formData.append("level_id", values.building_id);
			}

			if (values.level) {
				formData.append("room_id", values.level);
			}

			if (isEditMode) {
				// ✅ EDIT MODE - Single snag update
				const snag = values.snags[0];

				// Append snag fields
				formData.append("sector", snag.sector || "");
				formData.append("assigned_team_id", snag.assigned_team_id || "");
				formData.append("subsystem", snag.subsystem || "");
				formData.append("discipline", snag.discipline || "");
				formData.append("item", snag.item || "");
				formData.append("description", snag.description || "");
				formData.append("asset_tag_id", snag.asset_tag_id || "");
				formData.append("drawing_ref", snag.drawing_ref || "");
				formData.append("area_room", snag.area_room || "");
				formData.append("location", snag.location || "");
				formData.append("severity", snag.severity || "");
				formData.append("acceptance_criteria", snag.acceptance_criteria || "");
				formData.append("inspection_method", snag.inspection_method || "");
				formData.append("location_details", snag.location_details || "");
				formData.append("target_date", snag.target_date || "");

				// ✅ Append attachments to delete
				if (
					snag.delete_attachment_ids &&
					snag.delete_attachment_ids.length > 0
				) {
					formData.append(
						"delete_attachment_ids",
						JSON.stringify(snag.delete_attachment_ids),
					);
				}

				// ✅ Append new files
				if (snag.files && Array.isArray(snag.files) && snag.files.length > 0) {
					snag.files.forEach((file) => {
						if (file instanceof File) {
							formData.append("files", file);
						}
					});
				}

				console.log("Updating snag with ID:", editingSnagId);

				await updateSnag({ id: editingSnagId, formData }).unwrap();

				toast.success(trans("Snag updated successfully"), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				});
			} else {
				// ✅ CREATE MODE - Bulk create snags

				const snagsData = values.snags.map((snag, index) => {
					// Append files for this snag
					if (
						snag.files &&
						Array.isArray(snag.files) &&
						snag.files.length > 0
					) {
						snag.files.forEach((file) => {
							if (file instanceof File) {
								formData.append(`snags[${index}][files][]`, file);
							}
						});
					}

					// Return snag data object (without files)
					return {
						sector: snag.sector,
						assigned_team_id: snag.assigned_team_id,
						subsystem: snag.subsystem || "",
						discipline: snag.discipline || "",
						item: snag.item || "",
						description: snag.description || "",
						asset_tag_id: snag.asset_tag_id || "",
						drawing_ref: snag.drawing_ref || "",
						area_room: snag.area_room || "",
						location: snag.location || "",
						severity: snag.severity || "",
						acceptance_criteria: snag.acceptance_criteria || "",
						inspection_method: snag.inspection_method || "",
						location_details: snag.location_details || "",
						target_date: snag.target_date || "",
					};
				});

				formData.append("snags", JSON.stringify(snagsData));

				console.log("Creating snags - count:", snagsData.length);

				await bulkCreateSnags(formData).unwrap();

				toast.success(trans("Snags created successfully"), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				});
			}

			resetForm();
			setSnagForm(false);
			setEdited(true);
		} catch (err) {
			console.error("Form submit error:", err);
			let errorMsg = trans(
				(err?.data?.error &&
					Array.isArray(err.data.error) &&
					err.data.error[0]) ||
					err?.data?.message ||
					err?.response?.data?.message ||
					err?.message ||
					"Something went wrong",
			);
			toast.error(errorMsg, {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<Form show={snagForm} className={isMobile ? "form-snag-offcanvas" : ""}>
				<Offcanvas.Header>
					<FormHeader
						title={isEditMode ? trans("Edit Snag") : trans("Add new snag")}
						other={false}
						onClose={() => setSnagForm(false)}
					/>
				</Offcanvas.Header>
				<Offcanvas.Body className={isMobile ? "form-snag-offcanvas-body" : ""}>
					{isLoadingSnag || isFetchingSnag ? (
						<div
							className="d-flex justify-content-center align-items-center"
							style={{ minHeight: "200px" }}
						>
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : (
						<Formik
							initialValues={getInitialValues()}
							enableReinitialize={true}
							onSubmit={handleFormSubmit}
							validationSchema={validationSchema}
						>
							{({ handleSubmit, isSubmitting }) => (
								<FormFormik
									onSubmit={handleSubmit}
									dir={!isRTL ? "ltr" : "rtl"}
								>
									<SnagFormBody
										preselectedLocation={
											isEditMode
												? {
														project_id: getInitialValues().project_id,
														zone_id: getInitialValues().zone_id,
														building_id: getInitialValues().building_id,
														level: getInitialValues().level,
														project_name: getInitialValues().project_name,
														zone_name: getInitialValues().zone_name,
														building_name: getInitialValues().building_name,
														space_name: getInitialValues().space_name,
													}
												: preselectedLocation
										}
										isEditMode={isEditMode}
									/>
									{isMobile ? (
										<div className="form-snag-footer-sticky m-0">
											<div className="offcanvas-footer-body gap-8">
												<Button
													type={"primary"}
													htmlType="submit"
													loading={isSubmitting}
													disabled={isSubmitting}
												>
													{isSubmitting ? trans("Saving...") : trans("Save")}
												</Button>
												<Button
													type={"default"}
													htmlType="button"
													disabled={isSubmitting}
													onClick={() => setSnagForm(false)}
												>
													{trans("Cancel")}
												</Button>
											</div>
										</div>
									) : (
										<div className="offcanvas-footer m-0">
											<div className="offcanvas-footer-body gap-8">
												<Button
													type={"primary"}
													htmlType="submit"
													loading={isSubmitting}
													disabled={isSubmitting}
												>
													{isSubmitting ? trans("Saving...") : trans("Save")}
												</Button>
												<Button
													type={"default"}
													htmlType="button"
													disabled={isSubmitting}
													onClick={() => setSnagForm(false)}
												>
													{trans("Cancel")}
												</Button>
											</div>
										</div>
									)}
								</FormFormik>
							)}
						</Formik>
					)}
				</Offcanvas.Body>
			</Form>
		</>
	);
}
